import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Download, FileText, Lock, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { useCatalog } from "@/hooks/useCatalog";
import { api, errMsg, gbp } from "@/lib/api";

const DocCard = ({ d, i }) => {
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout/document", { slug: d.slug, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group grid gap-8 rounded-[28px] border border-stone-300 bg-white p-6 sm:grid-cols-[200px,1fr] sm:p-8"
      data-testid={`doc-card-${d.slug}`}
    >
      <div className="relative mx-auto w-48 self-start sm:w-full" style={{ perspective: 900 }}>
        <div className="absolute inset-x-4 bottom-0 h-6 rounded-full bg-black/25 blur-xl" />
        <img
          src={d.cover}
          alt={`${d.name} cover`}
          className="relative aspect-[1/1.414] w-full rounded-md object-cover shadow-2xl ring-1 ring-black/10 transition-transform duration-700 [transform:rotateY(-14deg)_rotateX(4deg)] group-hover:[transform:rotateY(0deg)_rotateX(0deg)_scale(1.03)]"
        />
      </div>
      <div className="flex flex-col">
        <p className="eyebrow text-[#9a7614]">{d.kind} · {d.pages} pages · PDF</p>
        <h3 className="mt-3 font-display text-2xl font-semibold leading-tight sm:text-3xl">{d.name}</h3>
        <p className="mt-3 text-sm text-stone-700">{d.summary}</p>
        <ul className="mt-5 grid gap-2 text-sm">
          {(d.includes ?? []).map((x) => (
            <li key={x} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#3E4A3D]" />{x}</li>
          ))}
        </ul>
        <div className="mt-auto flex flex-wrap items-center gap-4 pt-7">
          <span className="font-display text-4xl font-bold" data-testid={`doc-price-${d.slug}`}>{gbp(d.amount)}</span>
          <button onClick={buy} disabled={busy} className="btn-ink" data-testid={`doc-buy-${d.slug}`}>
            <Download className="h-4 w-4" /> {busy ? "Opening checkout…" : "Buy & download"}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const BundleCard = ({ d }) => {
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout/document", { slug: d.slug, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const save = (d.was ?? 0) - (d.amount ?? 0);
  return (
    <Reveal className="mb-20">
      <div className="relative grid items-center gap-10 overflow-hidden rounded-[32px] bg-[#141412] p-7 text-[#FAF8F5] sm:p-12 lg:grid-cols-12 grain" data-testid={`doc-card-${d.slug}`}>
        <div className="pointer-events-none absolute -left-20 -top-20 h-80 w-80 rounded-full bg-[#C59B27]/20 blur-[100px]" />
        <div className="relative lg:col-span-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#C59B27] px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#141412]"><Sparkles className="h-3.5 w-3.5" /> Best value · save {gbp(save)}</span>
          <h2 className="mt-5 font-display text-4xl font-bold leading-tight sm:text-5xl">{d.name}</h2>
          <p className="mt-4 max-w-lg text-stone-300">{d.summary}</p>
          <ul className="mt-6 grid gap-2 text-sm">
            {(d.includes ?? []).map((x) => <li key={x} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#C59B27]" />{x}</li>)}
          </ul>
          <div className="mt-8 flex flex-wrap items-end gap-5">
            <div>
              <span className="block text-sm text-stone-400 line-through" data-testid="bundle-was-price">{gbp(d.was)}</span>
              <span className="font-display text-5xl font-bold text-[#C59B27]" data-testid={`doc-price-${d.slug}`}>{gbp(d.amount)}</span>
            </div>
            <button onClick={buy} disabled={busy} className="btn-gold" data-testid={`doc-buy-${d.slug}`}>
              <Download className="h-4 w-4" /> {busy ? "Opening checkout…" : "Get all 4 documents"}
            </button>
          </div>
        </div>
        <motion.img
          src={d.cover}
          alt="All four Culifect documents"
          initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
          whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full lg:col-span-6"
        />
      </div>
    </Reveal>
  );
};

export default function Resources() {
  const { documents, loading, failed } = useCatalog();
  const bundle = documents.find((d) => d.bundle);
  const guides = documents.filter((d) => !d.bundle && d.kind?.startsWith("Guide"));
  const books = documents.filter((d) => !d.bundle && !d.kind?.startsWith("Guide"));
  return (
    <div data-testid="resources-page">
      <section className="relative overflow-hidden bg-[#141412] pb-20 pt-36 text-[#FAF8F5] sm:pt-44 grain">
        <div className="pointer-events-none absolute -right-40 top-10 h-[520px] w-[520px] rounded-full bg-[#C59B27]/15 blur-[130px]" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <p className="eyebrow text-[#C59B27]">Culifect Resources</p>
          <MaskedLines onMount delay={0.1} lines={["Kitchen know-how,", "ready to download."]} italic={1} className="mt-6 font-display text-5xl font-bold leading-[0.98] tracking-[-0.03em] sm:text-7xl lg:text-[6.5rem]" testId="resources-heading" />
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-2xl text-lg text-stone-300">Professional guides, checklists and recipe books from Culifect Catering and Chef Wilbert Chaniwa. Pay securely and download instantly.</p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-stone-300">
              <span className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#C59B27]" /> Secure Stripe checkout</span>
              <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#C59B27]" /> Instant PDF download</span>
              <span className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#C59B27]" /> Download links emailed to you</span>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="bg-[#FAF8F5] py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          {loading && <p className="text-stone-600">Loading resources…</p>}
          {failed && <p className="text-stone-700" data-testid="resources-error">Resources are unavailable right now. Please try again shortly.</p>}
          {bundle && <BundleCard d={bundle} />}
          {[["Kitchen set-up guides", guides], ["Recipe books by Chef Will", books]].map(([h, list]) =>
            list.length ? (
              <div key={h} className="mb-20 last:mb-0">
                <h2 className="mb-8 font-display text-3xl font-bold sm:text-5xl">{h}</h2>
                <div className="grid gap-6 lg:grid-cols-2">{list.map((d, i) => <DocCard key={d.slug} d={d} i={i} />)}</div>
              </div>
            ) : null,
          )}
          <p className="mt-10 text-xs text-stone-600">Prices include VAT where applicable. Digital downloads are for use within your own organisation. Guides summarise UK good practice and do not replace advice from your local authority.</p>
        </div>
      </section>
    </div>
  );
}
